import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
} from "react";
import { apiUrl, fetchWithAuth } from "./config/api";

const AuthContext = createContext(null);

const AUTH_KEYS = [
  "access",
  "refresh",
  "accessToken",
  "refreshToken",
  "access_token",
  "refresh_token",
  "token",
  "tokenData",
  "user",
  "auth",
];

export function AuthProvider({ children }) {
  // Load tokens from localStorage
  const [access, setAccess] = useState(
    localStorage.getItem("access_token") || null
  );
  const [refresh, setRefresh] = useState(
    localStorage.getItem("refresh_token") || null
  );
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(!!access);

  // Save tokens in one place, clean all legacy keys
  const saveTokens = useCallback((a, r) => {
    AUTH_KEYS.forEach((k) => localStorage.removeItem(k));

    if (a) {
      localStorage.setItem("access_token", a);
    }
    if (r) {
      localStorage.setItem("refresh_token", r);
    }

    setAccess(a || null);
    setRefresh(r || null);
  }, []);

  const logout = useCallback(() => {
    saveTokens(null, null);
    setProfile(null);
    setLoading(false);
  }, [saveTokens]);

  // Load user profile using current token (token resolution inside fetchWithAuth)
  const fetchUserProfile = useCallback(async () => {
    const res = await fetchWithAuth("/api/user-profile/");
    if (!res.ok) throw new Error("profile failed");
    const data = await res.json();
    setProfile(data);
  }, []);

  // Login and immediately load profile
  const login = async (username, password) => {
    try {
      const res = await fetch(apiUrl("/api/token/"), {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ username, password }),
      });

      if (!res.ok) return false;

      const data = await res.json();
      saveTokens(data.access, data.refresh);

      // Tiny delay to avoid any race
      await new Promise((resolve) => setTimeout(resolve, 10));

      await fetchUserProfile();
      return true;
    } catch (e) {
      console.error("Login error:", e);
      return false;
    }
  };

  // Refresh access token using refresh token
  const refreshAccessToken = useCallback(async () => {
    // Use state or localStorage as fallback
    const storedRefresh =
      refresh || localStorage.getItem("refresh_token") || null;

    if (!storedRefresh) {
      console.warn("No refresh token available");
      return null;
    }

    try {
      const res = await fetch(apiUrl("/api/token/refresh/"), {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ refresh: storedRefresh }),
      });

      // Only log out on real auth errors
      if (res.status === 401 || res.status === 403) {
        console.warn("Refresh token invalid/expired, logging out");
        logout();
        return null;
      }

      // For 500 / network errors: keep current access token
      if (!res.ok) {
        console.error("refreshAccessToken failed with status", res.status);
        return null;
      }

      const data = await res.json().catch(() => ({}));
      const newAccess = data.access || null;
      const newRefresh = data.refresh || storedRefresh;

      if (newAccess || newRefresh) {
        saveTokens(newAccess || access, newRefresh);
      }

      return newAccess;
    } catch (e) {
      console.error("refreshAccessToken error:", e);
      // network/server error → don't log out, just fail silently
      return null;
    }
  }, [refresh, access, logout, saveTokens]);

  // On mount / token change, load profile or refresh and retry
  useEffect(() => {
    (async () => {
      if (!access) return;
      setLoading(true);
      try {
        // First attempt: use current access token
        await fetchUserProfile();
      } catch (err) {
        console.warn("Initial profile load failed, trying refresh...", err);
        const newAccess = await refreshAccessToken();

        if (newAccess) {
          try {
            await fetchUserProfile();
          } catch (err2) {
            console.error("Profile load after refresh failed:", err2);
            // Do NOT logout here; keep existing session
          }
        } else {
          console.warn(
            "refreshAccessToken returned null, keeping existing session."
          );
        }
      } finally {
        setLoading(false);
      }
    })();
  }, [access, fetchUserProfile, refreshAccessToken]);

  return (
    <AuthContext.Provider
      value={{
        access,
        refresh,
        profile,
        loading,
        login,
        logout,
        refreshAccessToken,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}
